
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import type { SvgIconComponent } from '@mui/icons-material';
import { useState } from 'react';

import { useAppState } from '../reduxish/appState';
import CasinoSortEnum from '../models/CasinoSortEnum';

interface ISortOption {
    value: CasinoSortEnum;
    label: string;
    Icon: SvgIconComponent;
}

// Display labels are decoupled from the enum values (which double as
// persistence keys) so we can keep things short here without breaking
// stored prefs.
const SORT_OPTIONS: ISortOption[] = [
    { value: CasinoSortEnum.None, label: 'Default', Icon: ClearAllIcon },
    { value: CasinoSortEnum.Name, label: 'Name', Icon: SortByAlphaIcon },
    { value: CasinoSortEnum.Rating, label: 'Rating', Icon: StarRoundedIcon },
    { value: CasinoSortEnum.CrapsMinimumBet, label: 'Craps min bet', Icon: AttachMoneyIcon },
    { value: CasinoSortEnum.BlackjackMinimumBet, label: 'Blackjack min bet', Icon: AttachMoneyIcon },
    { value: CasinoSortEnum.RouletteMinimumBet, label: 'Roulette min bet', Icon: AttachMoneyIcon },
];


export default function SortDropDown() {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { state: { casinoSearch: { sortFieldType } }, dispatch } = useAppState();

    const open = Boolean(anchorEl);
    const handleSortIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleItemClick = (key: CasinoSortEnum) => {
        dispatch({ type: 'CASINO_SORT_FIELD_TYPE_UPDATE', sortFieldType: key })
        setAnchorEl(null);
    };

    const activeOption = SORT_OPTIONS.find(o => o.value === sortFieldType) ?? SORT_OPTIONS[0];
    const isCustomSort = activeOption.value !== CasinoSortEnum.None;
    const tooltipLabel = isCustomSort ? `Sort: ${activeOption.label}` : 'Sort';

    return (
        <>
            <Tooltip title={tooltipLabel}>
                <IconButton
                    type="button"
                    sx={{
                        p: '6px',
                        color: isCustomSort ? 'primary.main' : 'action.active',
                    }}
                    aria-label="sort"
                    id="sort-button"
                    aria-controls={open ? 'sort-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleSortIconClick}
                >
                    <SortIcon />
                </IconButton>
            </Tooltip>
            <Menu
                id="sort-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    list: { 'aria-labelledby': 'sort-button', dense: true, sx: { py: 0.5 } },
                    paper: {
                        elevation: 2,
                        sx: {
                            mt: 0.5,
                            minWidth: 200,
                            borderRadius: 2,
                            overflow: 'hidden',
                        },
                    },
                }}
            >
                {SORT_OPTIONS.map(({ value, label, Icon }) => {
                    const selected = value === sortFieldType;
                    return (
                        <MenuItem
                            key={value}
                            onClick={() => handleItemClick(value)}
                            selected={selected}
                            sx={{
                                color: selected ? 'primary.main' : 'text.primary',
                                '&.Mui-selected': {
                                    backgroundColor: 'transparent',
                                },
                                '&.Mui-selected:hover': {
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 32,
                                    color: selected ? 'primary.main' : 'action.active',
                                }}
                            >
                                <Icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={label}
                                slotProps={{
                                    primary: {
                                        sx: {
                                            fontSize: '0.875rem',
                                            fontWeight: selected ? 600 : 400,
                                        },
                                    },
                                }}
                            />
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    )
}